import Decimal from "decimal.js";
import { GraphQLClient, gql } from "graphql-request";
import { GetCartQuery } from "../types/storefront.generated";
import config from "./config";
import {
  BundleMetaobjectField,
  getRegionalBundleFieldPrice,
  isBundleFieldsAvailableForRegion,
} from "./bundlePricing";
import { ClubPricingContext, getCartClubPricingContext, getLineClubPrice } from "./clubPricing";

const bundleMetaobjectType = "bundles";

export type CheckoutPriceOverride = {
  price: string;
  quantity: number;
  discountNote?: string;
  restorePrice?: boolean;
};

export type CheckoutPriceOverrides = Record<string, CheckoutPriceOverride>;

// Keep bundle pricing aligned with club pricing: an applicable standard
// discount replaces the bundle discount, while Yotpo's eight-character codes
// retain it. This is evaluated from the current cart every time prices are
// calculated, so removing the code restores the bundle price automatically.
export function shouldApplyBundlePricing({
  hasDiscountCode,
  isYotpoDiscount,
}: Pick<ClubPricingContext, "hasDiscountCode" | "isYotpoDiscount">) {
  return !hasDiscountCode || isYotpoDiscount;
}

const affiliateDiscountQuery = gql`
  query AffiliateDiscount($code: String!) {
    codeDiscountNodeByCode(code: $code) {
      codeDiscount {
        ... on DiscountCodeBasic { tags }
        ... on DiscountCodeBxgy { tags }
        ... on DiscountCodeFreeShipping { tags }
      }
    }
  }
`;

type BundleProduct = {
  id: string;
  handle?: string | null;
  title?: string | null;
};

type BundleMetaobjectQuery = {
  metaobjects: {
    nodes: BundleMetaobject[];
  };
};

type BundleMetaobject = {
  id?: string;
  handle?: string;
  capabilities?: {
    publishable?: {
      status?: "ACTIVE" | "DRAFT" | string | null;
    } | null;
  } | null;
  fields: Array<{
    key: string;
    value?: string | null;
    jsonValue?: unknown;
    references?: {
      nodes: Array<
        | ({ __typename?: "Product" } & BundleProduct)
        | { __typename?: string }
      >;
    } | null;
  }>;
};

type StorefrontBundleMetaobjectQuery = {
  metaobjects: {
    nodes: Array<{
      id?: string;
      handle?: string;
      fields: Array<{
        key: string;
        value?: string | null;
        references?: {
          nodes: Array<
            | ({ __typename?: "Product" } & BundleProduct)
            | { __typename?: string }
          >;
        } | null;
      }>;
    }>;
  };
};

const adminBundleMetaobjectQuery = gql`
  query BundleMetaobjects($type: String!) {
    metaobjects(type: $type, first: 50) {
      nodes {
        id
        handle
        capabilities {
          publishable {
            status
          }
        }
        fields {
          key
          value
          jsonValue
          references(first: 20) {
            nodes {
              __typename
              ... on Product {
                id
                handle
                title
              }
            }
          }
        }
      }
    }
  }
`;

const storefrontBundleMetaobjectQuery = gql`
  query BundleMetaobjects($type: String!) {
    metaobjects(type: $type, first: 50) {
      nodes {
        id
        handle
        fields {
          key
          value
          references(first: 20) {
            nodes {
              __typename
              ... on Product {
                id
                handle
                title
              }
            }
          }
        }
      }
    }
  }
`;

// Bundle naming:
// - Metaobject bundle: the single bundle object stored on cart.bundles. It is
//   displayed as checkout.bundleItems and converted into draft order lines in
//   getOrderFromCart.ts.
// - Line item bundle: normal cart line items that qualify for the bundle
//   discount described by the configured bundle metaobject below. This helper
//   only returns price overrides for that line item bundle path.
export async function getBundlePriceOverrides(cart: GetCartQuery["cart"]): Promise<CheckoutPriceOverrides> {
  if (!cart) {
    return {};
  }

  const affiliateOverrides = await getAffiliatePriceOverrides(cart);
  const clubOverrides = getClubPriceOverrides(cart);
  if (!shouldApplyBundlePricing(getCartClubPricingContext(cart))) {
    return { ...clubOverrides, ...affiliateOverrides };
  }

  try {
    const bundles = await getBundleMetaobjects();
    const bundleOverrides = bundles.reduce<CheckoutPriceOverrides>((overrides, bundle) => {
      if (!isActiveMetaobject(bundle) || !isBundleFieldsAvailableForRegion(bundle.fields)) {
        return overrides;
      }

      const price = getRegionalBundleFieldPrice(bundle.fields);
      const discountNote = getBundleTitle(bundle.fields);
      const products = getBundleProducts(bundle.fields);
      const quantity = getBundleQuantity(bundle.fields);
      const mixAndMatch = getBundleMixAndMatch(bundle.fields);

      if (!price || products.length === 0) {
        return overrides;
      }

      return mergeCheckoutPriceOverrides(
        overrides,
        getLineItemBundlePriceOverrides(cart, products, price, discountNote, quantity, mixAndMatch),
        cart,
      );
    }, {});

    return { ...bundleOverrides, ...clubOverrides, ...affiliateOverrides };
  } catch (error) {
    console.error("Unable to load bundle price overrides", error);
    return { ...clubOverrides, ...affiliateOverrides };
  }
}

function getClubPriceOverrides(cart: NonNullable<GetCartQuery["cart"]>): CheckoutPriceOverrides {
  const pricingContext = getCartClubPricingContext(cart);
  if (!pricingContext.isClubPricingDiscount) {
    return {};
  }

  return cart.lines.nodes.reduce<CheckoutPriceOverrides>((overrides, line) => {
    if (!("merchandise" in line)) {
      return overrides;
    }

    const clubPrice = getLineClubPrice(
      line,
      pricingContext.activeClub,
      pricingContext.customerId,
      pricingContext.hasDiscountCode,
      pricingContext.isYotpoDiscount,
      pricingContext.isClubPricingDiscount,
    );
    if (!clubPrice || clubPrice.greaterThanOrEqualTo(line.merchandise.price.amount)) {
      return overrides;
    }

    overrides[line.merchandise.id] = {
      price: clubPrice.toFixed(2),
      quantity: line.quantity,
      discountNote: "Club price",
    };
    return overrides;
  }, {});
}

async function getAffiliatePriceOverrides(cart: NonNullable<GetCartQuery["cart"]>) {
  if (!(await getAffiliateDiscountCode(cart))) {
    return {};
  }

  return cart.lines.nodes.reduce<CheckoutPriceOverrides>((overrides, line) => {
    if (!("merchandise" in line)) {
      return overrides;
    }

    const merchandise = line.merchandise;
    const price = merchandise.affiliatePrice?.value ?? merchandise.product.affiliatePrice?.value;
    const affiliatePrice = parseAffiliatePrice(price);
    if (!affiliatePrice) {
      return overrides;
    }
    if (affiliatePrice.lessThanOrEqualTo(0) || affiliatePrice.greaterThanOrEqualTo(merchandise.price.amount)) {
      return overrides;
    }

    overrides[merchandise.id] = {
      price: affiliatePrice.toFixed(2),
      quantity: line.quantity,
      discountNote: "Affiliate price",
    };
    return overrides;
  }, {});
}

function parseAffiliatePrice(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  try {
    return new Decimal(value);
  } catch {
    try {
      const money = JSON.parse(value);
      return typeof money.amount === "string" || typeof money.amount === "number"
        ? new Decimal(money.amount)
        : null;
    } catch {
      return null;
    }
  }
}

export async function getAffiliateDiscountCode(cart: GetCartQuery["cart"]) {
  if (!cart) {
    return null;
  }

  for (const discountCode of cart.discountCodes) {
    if (discountCode.applicable && await isAffiliateDiscountCode(discountCode.code)) {
      return discountCode.code;
    }
  }

  return null;
}

async function isAffiliateDiscountCode(code: string) {
  if (!config.shopifyAdminUrl || !config.shopifyAppPassword) {
    return false;
  }

  try {
    const client = new GraphQLClient(config.shopifyAdminUrl, {
      headers: { "X-Shopify-Access-Token": config.shopifyAppPassword },
    });
    const result = await client.request<{
      codeDiscountNodeByCode?: { codeDiscount?: { tags?: string[] } | null } | null;
    }>(
      affiliateDiscountQuery,
      { code },
    );
    return result.codeDiscountNodeByCode?.codeDiscount?.tags?.some(
      (tag) => tag.toLowerCase() === "affiliate",
    ) ?? false;
  } catch (error) {
    console.error("Unable to check affiliate discount", error);
    return false;
  }
}

async function getBundleMetaobjects(): Promise<BundleMetaobject[]> {
  if (config.shopifyAdminUrl && config.shopifyAppPassword) {
    const client = new GraphQLClient(config.shopifyAdminUrl, {
      headers: {
        "X-Shopify-Access-Token": config.shopifyAppPassword,
        "Content-Type": "application/json",
      },
    });

    const response = await client.request<BundleMetaobjectQuery>(adminBundleMetaobjectQuery, {
      type: bundleMetaobjectType,
    });

    return response.metaobjects.nodes;
  }

  if (config.shopifyStorefrontUrl && config.shopifyAccessToken) {
    const client = new GraphQLClient(config.shopifyStorefrontUrl, {
      headers: {
        "X-Shopify-Storefront-Access-Token": config.shopifyAccessToken,
        "Content-Type": "application/json",
      },
    });

    const response = await client.request<StorefrontBundleMetaobjectQuery>(storefrontBundleMetaobjectQuery, {
      type: bundleMetaobjectType,
    });

    return response.metaobjects.nodes;
  }

  return [];
}

function isActiveMetaobject(metaobject: BundleMetaobject) {
  const status = metaobject.capabilities?.publishable?.status;
  if (metaobject.capabilities) {
    return status === "ACTIVE";
  }
  return true;
}

function getBundleTitle(fields: BundleMetaobject["fields"]) {
  return getField(fields, "product_bundle_builder")?.value ?? getField(fields, "title")?.value ?? "Bundle discount";
}

function getBundleProducts(fields: BundleMetaobject["fields"]): BundleProduct[] {
  const field = getField(fields, "product_bundle_selector");
  const referencedProducts = field?.references?.nodes.filter(
    (node): node is { __typename?: "Product" } & BundleProduct => node.__typename === "Product",
  ) ?? [];

  if (referencedProducts.length > 0) {
    return referencedProducts;
  }

  return parseProducts(field?.jsonValue ?? field?.value);
}

function getBundleQuantity(fields: BundleMetaobject["fields"]) {
  const field = getField(fields, "quantity");
  const value = field?.jsonValue ?? field?.value;
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const quantity = Math.floor(Number(value));
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return null;
  }

  return quantity;
}

function getBundleMixAndMatch(fields: BundleMetaobject["fields"]) {
  const field = getField(fields, "mix_and_match");
  return parseBoolean(field?.jsonValue ?? field?.value);
}

function getField<T extends BundleMetaobjectField>(fields: T[], key: string) {
  return fields.find((field) => field.key === key) ?? null;
}

function parseProducts(value: unknown): BundleProduct[] {
  if (!value) {
    return [];
  }

  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map((product) => {
      if (typeof product === "string") {
        return { id: product };
      }
      return {
        id: product.id ?? product.gid ?? "",
        handle: product.handle,
        title: product.title,
      };
    }).filter((product) => product.id || product.handle || product.title);
  } catch {
    return [];
  }
}

function getLineItemBundlePriceOverrides(
  cart: NonNullable<GetCartQuery["cart"]>,
  products: BundleProduct[],
  bundlePrice: string | number,
  discountNote: string,
  requiredQuantity: number | null,
  mixAndMatch: boolean,
): CheckoutPriceOverrides {
  const cartLines = cart.lines.nodes.filter((line): line is LineWithMerchandise => {
    return "merchandise" in line;
  });
  const pricingContext = getCartClubPricingContext(cart);

  if (products.length === 1) {
    if (!requiredQuantity || requiredQuantity < 2) {
      return {};
    }

    return getQuantityBundlePriceOverrides(
      cartLines,
      (line) => productMatches(line.merchandise.product, products),
      bundlePrice,
      discountNote,
      requiredQuantity,
      pricingContext,
    );
  }

  if (mixAndMatch) {
    if (!requiredQuantity || requiredQuantity <= 0) {
      return {};
    }

    return getQuantityBundlePriceOverrides(
      cartLines,
      (line) => productMatches(line.merchandise.product, products),
      bundlePrice,
      discountNote,
      requiredQuantity,
      pricingContext,
    );
  }

  const remainingQuantities = new Map<number, number>(
    cartLines.map((line, index) => [index, line.quantity]),
  );
  const appliedDiscounts = new Map<string, {
    line: LineWithMerchandise;
    quantity: number;
    discount: Decimal;
  }>();
  if (products.length === 0) {
    return {};
  }

  while (true) {
    const bundleRemainingQuantities = new Map(remainingQuantities);
    const bundleLines: LineWithMerchandise[] = [];

    for (const product of products) {
      const lineIndex = cartLines.findIndex((line, index) => {
        const remainingQuantity = bundleRemainingQuantities.get(index) ?? 0;
        return remainingQuantity > 0 && productMatches(line.merchandise.product, [product]);
      });

      if (lineIndex < 0) {
        return buildOverrides(appliedDiscounts, discountNote, pricingContext);
      }

      bundleRemainingQuantities.set(
        lineIndex,
        (bundleRemainingQuantities.get(lineIndex) ?? 0) - 1,
      );
      bundleLines.push(cartLines[lineIndex]);
    }

    const effectiveBundleTotal = bundleLines.reduce((total, line) => {
      return total.plus(getEffectiveLinePrice(line, pricingContext));
    }, new Decimal(0));
    const totalDiscount = effectiveBundleTotal.minus(bundlePrice);

    if (totalDiscount.lessThanOrEqualTo(0)) {
      return buildOverrides(appliedDiscounts, discountNote, pricingContext);
    }

    const discountPerItem = totalDiscount.div(bundleLines.length);
    bundleLines.forEach((line) => {
      const existingDiscount = appliedDiscounts.get(line.merchandise.id);
      if (existingDiscount) {
        existingDiscount.quantity += 1;
        existingDiscount.discount = existingDiscount.discount.plus(discountPerItem);
        return;
      }

      appliedDiscounts.set(line.merchandise.id, {
        line,
        quantity: 1,
        discount: discountPerItem,
      });
    });

    bundleRemainingQuantities.forEach((quantity, lineIndex) => {
      remainingQuantities.set(lineIndex, quantity);
    });
  }
}

function getQuantityBundlePriceOverrides(
  cartLines: LineWithMerchandise[],
  lineMatchesBundle: (line: LineWithMerchandise) => boolean,
  bundlePrice: string | number,
  discountNote: string,
  requiredQuantity: number,
  pricingContext: ClubPricingContext,
) {
  const matchingUnits = cartLines.flatMap((line) => {
    if (!lineMatchesBundle(line)) {
      return [];
    }

    return Array.from({ length: line.quantity }, () => line);
  });
  const eligibleQuantity = Math.floor(matchingUnits.length / requiredQuantity) * requiredQuantity;
  const appliedDiscounts = new Map<string, {
    line: LineWithMerchandise;
    quantity: number;
    discount: Decimal;
  }>();

  for (let index = 0; index < eligibleQuantity; index += requiredQuantity) {
    const bundleLines = matchingUnits.slice(index, index + requiredQuantity);
    const effectiveBundleTotal = bundleLines.reduce((total, line) => {
      return total.plus(getEffectiveLinePrice(line, pricingContext));
    }, new Decimal(0));
    const totalDiscount = effectiveBundleTotal.minus(bundlePrice);

    if (totalDiscount.lessThanOrEqualTo(0)) {
      continue;
    }

    const discountPerItem = totalDiscount.div(requiredQuantity);
    bundleLines.forEach((line) => {
      const existingDiscount = appliedDiscounts.get(line.merchandise.id);
      if (existingDiscount) {
        existingDiscount.quantity += 1;
        existingDiscount.discount = existingDiscount.discount.plus(discountPerItem);
        return;
      }

      appliedDiscounts.set(line.merchandise.id, {
        line,
        quantity: 1,
        discount: discountPerItem,
      });
    });
  }

  return buildOverrides(appliedDiscounts, discountNote, pricingContext);
}

function mergeCheckoutPriceOverrides(
  existingOverrides: CheckoutPriceOverrides,
  incomingOverrides: CheckoutPriceOverrides,
  cart: NonNullable<GetCartQuery["cart"]>,
) {
  const merged = { ...existingOverrides };

  Object.entries(incomingOverrides).forEach(([variantId, incomingOverride]) => {
    const existingOverride = merged[variantId];
    if (!existingOverride) {
      merged[variantId] = incomingOverride;
      return;
    }

    merged[variantId] = getPreferredOverride(
      existingOverride,
      incomingOverride,
      getCartLineQuantity(cart, variantId),
    );
  });

  return merged;
}

function getPreferredOverride(
  existingOverride: CheckoutPriceOverride,
  incomingOverride: CheckoutPriceOverride,
  lineQuantity: number,
) {
  const existingSplitsLine = existingOverride.quantity < lineQuantity;
  const incomingSplitsLine = incomingOverride.quantity < lineQuantity;

  if (existingSplitsLine !== incomingSplitsLine) {
    return existingSplitsLine ? existingOverride : incomingOverride;
  }

  if (existingSplitsLine && incomingSplitsLine && existingOverride.quantity !== incomingOverride.quantity) {
    return existingOverride.quantity > incomingOverride.quantity ? existingOverride : incomingOverride;
  }

  return new Decimal(incomingOverride.price).lessThan(existingOverride.price)
    ? incomingOverride
    : existingOverride;
}

function getCartLineQuantity(cart: NonNullable<GetCartQuery["cart"]>, variantId: string) {
  const line = cart.lines.nodes.find((cartLine) => {
    return "merchandise" in cartLine && cartLine.merchandise.id === variantId;
  });

  return line && "merchandise" in line ? line.quantity : 0;
}

function productMatches(product: { id: string; handle: string; title: string }, products: BundleProduct[]) {
  return products.some((bundleProduct) => {
    return bundleProduct.id === product.id ||
      bundleProduct.handle === product.handle ||
      bundleProduct.title === product.title;
  });
}

function parseBoolean(value: unknown) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }
  return false;
}

type LineWithMerchandise = Extract<NonNullable<GetCartQuery["cart"]>["lines"]["nodes"][number], { merchandise: unknown }>;

function getEffectiveLinePrice(
  line: LineWithMerchandise,
  pricingContext: ClubPricingContext,
) {
  const linePrice = new Decimal(line.merchandise.price.amount);
  const clubPrice = getLineClubPrice(line, pricingContext.activeClub, "bundle-pricing", false);

  return clubPrice ?? linePrice;
}

function buildOverrides(
  appliedDiscounts: Map<string, {
    line: LineWithMerchandise;
    quantity: number;
    discount: Decimal;
  }>,
  discountNote: string,
  pricingContext: ClubPricingContext,
) {
  const overrides: CheckoutPriceOverrides = {};

  appliedDiscounts.forEach(({ line, quantity, discount }) => {
    const originalPrice = getEffectiveLinePrice(line, pricingContext);
    const averageDiscount = discount.div(quantity);
    const price = Decimal.max(originalPrice.minus(averageDiscount), 0)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .toFixed(2);

    overrides[line.merchandise.id] = {
      price,
      quantity,
      discountNote,
    };
  });

  return overrides;
}
